import { usePageScrollReset } from "./hooks/usePageScrollReset";
import { TeamPage } from "./pages/TeamPage";
import { OrgInvitePage } from "./pages/OrgInvitePage";
import { useAsyncCallback } from "./hooks/useAsyncCallback";
import { Fragment, useEffect, useState } from "react";
import {
  clearAuthState,
  consumeAuthNotice,
  getAccessToken,
  setPostLoginRedirectPath,
} from "./lib/authStorage";
import { isProtectedRoute, parseRoute } from "./lib/router";
import { LoaderCircle } from "lucide-react";
import { Button } from "@components/ui/button";
import { AuthPage } from "./pages/AuthPage";
import { DashboardPage } from "./pages/DashboardPage";
import { InboxPage } from "./pages/InboxPage";
import { ReviewQueuePage } from "./pages/ReviewQueuePage";
import { LeadIntakePage } from "./pages/LeadIntakePage";
import { EntityDetailPage } from "./pages/EntityDetailPage";
import { ThreadDetailPage } from "./pages/ThreadDetailPage";
import { LensShellPage } from "./pages/LensShellPage";
import { CustomerProfilePage } from "./pages/CustomerProfilePage";
import { TriageRulesPage } from "./pages/TriageRulesPage";
import { SlaRulesPage } from "./pages/SlaRulesPage";
import { SecurityPage } from "./pages/SecurityPage";
import { SettingsPage } from "./pages/SettingsPage";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import { BillingPage } from "./pages/BillingPage";
import { RescueRoomPage } from "./pages/RescueRoomPage";
import { OrgSetupPage } from "./pages/OrgSetupPage";
import { ObligationsPage } from "./pages/ObligationsPage";
import { AiEvaluationPage } from "./pages/AiEvaluationPage";
import { Toaster } from "@components/ui/sonner";
import { SidebarInset, SidebarProvider } from "@components/ui/sidebar";
import { AppSidebar } from "@components/layout/AppSidebar";
import { OrgProvider } from "@components/layout/OrgContext";
import { UserPreferencesProvider } from "@components/layout/UserPreferencesProvider";
import { GlobalSearch } from "@components/search/GlobalSearch";
import { useOrgContext } from "@components/layout/OrgContext";
import {
  canAccessRouteForRoleView,
  getDefaultRouteForRoleView,
  getRoleViewFromOrgRole,
} from "./lib/roleViews";
import { logoutSession } from "./lib/authSession";

type AppShellProps = {
  isAuthenticated: boolean;
};

function AppShell({ isAuthenticated }: AppShellProps) {
  const [route, setRoute] = useState(() => window.location.pathname || "/");
  const [routeEntry, setRouteEntry] = useState(0);
  usePageScrollReset(route);
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const {
    currentOrg,
    currentMembership,
    isLoading: isOrgLoading,
    isSwitching,
    hasLoaded,
    error: orgContextError,
    refreshOrgs,
  } = useOrgContext();
  const roleView = getRoleViewFromOrgRole(currentMembership?.role);

  useEffect(() => { setIsSearchOpen(false); }, [currentOrg?.id]);

  useEffect(() => {
    window.dispatchEvent(new Event("iospoke:pageview"));
  }, [route]);

  const navigate = (nextRoute: string) => {
    if (nextRoute === `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      return;
    }
    window.history.pushState({}, "", nextRoute);
    const nextPath = new URL(nextRoute, window.location.origin).pathname;
    if (nextPath === "/billing" || nextPath === "/invite") setRouteEntry((entry) => entry + 1);
    setRoute(nextPath);
  };

  useEffect(() => {
    const handlePopState = () => {
      if (["/billing", "/invite"].includes(window.location.pathname)) setRouteEntry((entry) => entry + 1);
      setRoute(window.location.pathname || "/");
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    const routeInfo = parseRoute(route);
    if (isProtectedRoute(routeInfo.name) && !isAuthenticated) {
      const noticeMap: Record<string, string> = {
        auth: "Please sign in to continue.",
        dashboard: "Please sign in to access the dashboard.",
        rescues: "Please sign in to access Rescue Room.",
        obligations: "Please sign in to access obligations.",
        aiEvaluation: "Please sign in to access AI evaluation.",
        security: "Please sign in to access security settings.",
        inbox: "Please sign in to access the inbox.",
        review: "Please sign in to access the review queue.",
        leads: "Please sign in to access leads.",
        entity: "Please sign in to access work items.",
        thread: "Please sign in to access conversations.",
        customer: "Please sign in to access customer profiles.",
        triageRules: "Please sign in to access triage rules.",
        slaRules: "Please sign in to access SLA rules.",
        lens: "Please sign in to access lenses.",
        settings: "Please sign in to access settings.",
        team: "Please sign in to invite a teammate.",
        orgInvite: "Create your account or sign in with the invited email to join the workspace.",
        integrations: "Please sign in to access integrations.",
        billing: "Please sign in to access billing settings.",
      };
      setPostLoginRedirectPath(route, window.location.search);
      setAuthNotice(noticeMap[routeInfo.name]);
      navigate("/");
      return;
    }
  }, [isAuthenticated, route]);

  useEffect(() => {
    if (!isAuthenticated || isOrgLoading || isSwitching || orgContextError) {
      return;
    }
    if (!hasLoaded) {
      return;
    }
    const routeInfo = parseRoute(route);
    const defaultRoleRoute = getDefaultRouteForRoleView(roleView);

    if (routeInfo.name === "orgInvite") return;
    if (!currentOrg && routeInfo.name !== "orgSetup") {
      navigate("/orgs/setup");
      return;
    }
    if (currentOrg && route === "/") {
      navigate(defaultRoleRoute);
      return;
    }
    if (currentOrg && routeInfo.name === "auth") {
      navigate(defaultRoleRoute);
      return;
    }
    if (currentOrg && routeInfo.name === "orgSetup") {
      navigate(defaultRoleRoute);
      return;
    }
    // The billing page validates reminder membership and resolves its workspace first.
    const resolvingBillingWorkspace = routeInfo.name === "billing"
      && new URLSearchParams(window.location.search).has("org");
    if (currentOrg && !resolvingBillingWorkspace && !canAccessRouteForRoleView(roleView, routeInfo.name)) {
      navigate(defaultRoleRoute);
    }
  }, [currentOrg, hasLoaded, isAuthenticated, isOrgLoading, isSwitching, orgContextError, roleView, route]);

  useEffect(() => {
    const notice = consumeAuthNotice();
    if (notice) {
      setAuthNotice(notice);
    }
  }, [route]);

  const { pending: handleSignOutPending, callback: handleSignOut } = useAsyncCallback(async () => {
    try {
      await logoutSession();
    } catch {
      // Local credentials must still be removed when the server is unreachable.
    } finally {
      clearAuthState();
      setAuthNotice("You have been signed out.");
      navigate("/");
    }
  });

  const routeInfo = parseRoute(route);
  const showSidebar =
    isAuthenticated && !orgContextError && !["auth", "orgSetup", "orgInvite"].includes(routeInfo.name);

  let content = isProtectedRoute(routeInfo.name) && !isAuthenticated ? (
    <p role="status" className="p-6 text-sm text-muted-foreground">Opening sign in…</p>
  ) : (
    <AuthPage
      notice={authNotice}
      onNoticeChange={setAuthNotice}
      navigate={navigate}
    />
  );

  if (isAuthenticated && (!hasLoaded || isOrgLoading || isSwitching)) {
    content = (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        <LoaderCircle className="h-6 w-6 animate-spin" />
        <span className="sr-only">Loading workspace</span>
      </div>
    );
  }

  if (isAuthenticated && hasLoaded && orgContextError) {
    content = (
      <main className="flex min-h-screen items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-md rounded-lg border bg-card p-6 text-center shadow-sm">
          <h1 className="text-xl font-semibold">Unable to load your workspace</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Your workspace may still be available. Try loading it again before creating or changing anything.
          </p>
          <Button className="mt-5" onClick={() => void refreshOrgs()} disabled={isOrgLoading}>
            Try again
          </Button>
        </div>
      </main>
    );
  }

  const canRenderOrgRoutes = !isAuthenticated || (!isOrgLoading && !isSwitching && hasLoaded && !orgContextError);

  if (routeInfo.name === "orgInvite" && isAuthenticated && hasLoaded && !isOrgLoading) {
    content = <OrgInvitePage key={routeEntry} navigate={navigate} onUseDifferentAccount={() => {
      setPostLoginRedirectPath("/invite", window.location.search);
      return handleSignOut();
    }} />;
  }

  if (canRenderOrgRoutes && routeInfo.name === "dashboard" && isAuthenticated) {
    content = <DashboardPage navigate={navigate} roleView={roleView} />;
  }

  if (canRenderOrgRoutes && routeInfo.name === "rescues" && isAuthenticated) {
    content = <RescueRoomPage key={currentOrg?.id} rescueCaseId={routeInfo.params.rescueCaseId} navigate={navigate} />;
  }

  if (canRenderOrgRoutes && routeInfo.name === "obligations" && isAuthenticated) {
    content = <ObligationsPage navigate={navigate} />;
  }
  if (canRenderOrgRoutes && routeInfo.name === "aiEvaluation" && isAuthenticated) content = <AiEvaluationPage navigate={navigate} />;

  if (canRenderOrgRoutes && routeInfo.name === "security" && isAuthenticated) {
    content = <SecurityPage navigate={navigate} />;
  }

  if (canRenderOrgRoutes && routeInfo.name === "inbox" && isAuthenticated) {
    content = <InboxPage navigate={navigate} />;
  }

  if (canRenderOrgRoutes && routeInfo.name === "review" && isAuthenticated) {
    content = <ReviewQueuePage navigate={navigate} />;
  }

  if (canRenderOrgRoutes && routeInfo.name === "leads" && isAuthenticated) {
    content = <LeadIntakePage navigate={navigate} />;
  }

  if (canRenderOrgRoutes && routeInfo.name === "entity" && isAuthenticated) {
    content = (
      <EntityDetailPage
        entityId={routeInfo.params.entityId ?? ""}
        navigate={navigate}
      />
    );
  }

  if (canRenderOrgRoutes && routeInfo.name === "thread" && isAuthenticated) {
    content = (
      <ThreadDetailPage
        threadId={routeInfo.params.threadId ?? ""}
        navigate={navigate}
        onNoticeChange={setAuthNotice}
      />
    );
  }

  if (canRenderOrgRoutes && routeInfo.name === "customer" && isAuthenticated) {
    content = (
      <CustomerProfilePage
        customerId={routeInfo.params.customerId ?? ""}
        navigate={navigate}
      />
    );
  }

  if (canRenderOrgRoutes && routeInfo.name === "triageRules" && isAuthenticated) {
    content = <TriageRulesPage key={currentOrg?.id} navigate={navigate} />;
  }

  if (canRenderOrgRoutes && routeInfo.name === "slaRules" && isAuthenticated) {
    content = <SlaRulesPage navigate={navigate} />;
  }

  if (canRenderOrgRoutes && routeInfo.name === "lens" && isAuthenticated) {
    content = (
      <LensShellPage
        lensKey={routeInfo.params.lensKey ?? ""}
        navigate={navigate}
      />
    );
  }

  if (canRenderOrgRoutes && routeInfo.name === "settings" && isAuthenticated) {
    content = <SettingsPage navigate={navigate} />;
  }

  if (canRenderOrgRoutes && routeInfo.name === "team" && isAuthenticated) {
    content = <TeamPage navigate={navigate} />;
  }

  if (canRenderOrgRoutes && routeInfo.name === "integrations" && isAuthenticated) {
    content = <IntegrationsPage navigate={navigate} />;
  }

  if (canRenderOrgRoutes && routeInfo.name === "billing" && isAuthenticated) {
    content = <BillingPage key={routeEntry} navigate={navigate} />;
  }

  if (routeInfo.name === "orgSetup" && isAuthenticated && hasLoaded) {
    content = <OrgSetupPage navigate={navigate} />;
  }

  const workspaceContent = <Fragment key={`workspace:${currentOrg?.id ?? "none"}`}>{content}</Fragment>;
  const appBody = showSidebar ? (
    <SidebarProvider defaultOpen>
      <AppSidebar
        key={`sidebar:${currentOrg?.id ?? "none"}`}
        routeName={routeInfo.name}
        currentPath={route}
        roleView={roleView}
        navigate={navigate}
        onOpenSearch={() => setIsSearchOpen(true)}
        onSignOut={handleSignOut}
        isSigningOut={handleSignOutPending}
        orgId={currentOrg?.id}
      />
      <SidebarInset>
        <GlobalSearch
          key={`search:${currentOrg?.id ?? "none"}`}
          open={isSearchOpen}
          onOpenChange={setIsSearchOpen}
          onNavigate={navigate}
        />
        <Toaster closeButton position="top-right" />
        {workspaceContent}
      </SidebarInset>
    </SidebarProvider>
  ) : (
    <>
      <Toaster closeButton position="top-right" />
      {workspaceContent}
    </>
  );

  return (
    appBody
  );
}

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    Boolean(getAccessToken())
  );

  useEffect(() => {
    const handleAuthChange = () => {
      setIsAuthenticated(Boolean(getAccessToken()));
    };
    handleAuthChange();
    window.addEventListener("auth:changed", handleAuthChange);
    window.addEventListener("storage", handleAuthChange);
    return () => {
      window.removeEventListener("auth:changed", handleAuthChange);
      window.removeEventListener("storage", handleAuthChange);
    };
  }, []);

  return (
    <UserPreferencesProvider isAuthenticated={isAuthenticated}>
      <OrgProvider isAuthenticated={isAuthenticated}>
        <AppShell isAuthenticated={isAuthenticated} />
      </OrgProvider>
    </UserPreferencesProvider>
  );
}
